import React from 'react';
import { useTranslation } from 'react-i18next';
import { Expandable } from '@AMIEWEB/Common';
import { OrderDetailsReportOrderInfo } from './OrderDetailsReportOrderInfo';
import { OrderDetailsReportRFORequirements } from './OrderDetailsReportRFORequirements';
import { OrderDetailsReportLTRestrictions } from './OrderDetailsReportLTRestrictions';
import { OrderDetailsReportPayBonusInfo } from './OrderDetailsReportPayBonusInfo';
import { OrderDetailsReportMealsInfo } from './OrderDetailsReportMealsInfo';
import { OrderDetailsReportHousingInfo } from './OrderDetailsReportHousingInfo';
import { OrderDetailsReportFacilityInfo } from './OrderDetailsReportFacilityInfo';
import { OrderDetailsReportUnitInfo } from './OrderDetailsReportUnitInfo';
import { OrderDetailsReportTravelInfo } from './OrderDetailsReportTravelInfo';
import { OrderDetailsReportPointOfInterest as OrderDetailsReportPointOfInterest } from './OrderDetailsReportPointOfInterest';
import { IOrderReportDetails } from 'app/models/Orders/OrderDetails';
import {
  flattenFacilityDetails,
  flattenHousingDetails,
  flattenLTRestrictionDetails,
  flattenMealDetails,
  flattenOrderInfoDetails,
  flattenPayBonusDetails,
  flattenPointOfIntrests,
  flattenTravelDetails,
  flattenUnitDetails,
  flattenrfoDetails,
} from './OrderDetailsReportUtilities';
import { GenericTabs, Orientations } from '@AMIEWEB/Common/Tabs/GenericTabs';
import { Typography } from '@mui/material';

interface Props {
  orderReportDetails: IOrderReportDetails | undefined;
}
export const OrderDetailReportWrapper = (props: Props) => {
  const { t } = useTranslation();
  const orderReportDetails = props.orderReportDetails;

  const orderInfoDetails = flattenOrderInfoDetails(orderReportDetails);
  const rfoDetails = flattenrfoDetails(orderReportDetails);
  const lTRestrictionDetails = flattenLTRestrictionDetails(orderReportDetails);
  const payBonusDetails = flattenPayBonusDetails(orderReportDetails);
  const mealDetails = flattenMealDetails(orderReportDetails);
  const housingDetails = flattenHousingDetails(orderReportDetails);
  const facilityDetails = flattenFacilityDetails(orderReportDetails);
  const unitDetails = flattenUnitDetails(orderReportDetails);
  const travelDetails = flattenTravelDetails(orderReportDetails);
  const pointOfIntrests = flattenPointOfIntrests(orderReportDetails);

  const currentDate = new Date();
  const formatCurrentDate = `${currentDate.toLocaleString('en-us', {
    month: 'long',
    day: 'numeric',
  })}, ${currentDate.getFullYear()}`;

  const tabs = [
    {
      label: `${t('order.orderReportDetails.tab.orderinformation')}`,
      component: (
        <>
          <Typography style={{ fontWeight: 500, fontSize: 18 }}>{t('Order Detail Report')}</Typography>
          <Typography style={{ fontWeight: 400, fontSize: 14, paddingBottom: 20 }}>{formatCurrentDate}</Typography>
          <Expandable variant="report" expanded titleComponent={t('order.orderReportDetails.tab.orderinformation')}>
            <OrderDetailsReportOrderInfo orderInfoDetails={orderInfoDetails} />
          </Expandable>
        </>
      ),
    },
    {
      label: `${t('order.orderReportDetails.tab.rforequirements')}`,
      component: (
        <Expandable variant="report" expanded titleComponent={t('order.orderReportDetails.tab.rforequirements')}>
          <OrderDetailsReportRFORequirements rfoDetails={rfoDetails} />
        </Expandable>
      ),
    },
    {
      label: `${t('order.orderReportDetails.tab.localtravelerrestrictions')}`,
      component: (
        <Expandable variant="report" expanded titleComponent={t('order.orderReportDetails.tab.localtravelerrestrictions')}>
          <OrderDetailsReportLTRestrictions ltrestrictionDetails={lTRestrictionDetails} />
        </Expandable>
      ),
    },
    {
      label: `${t('order.orderReportDetails.tab.paybonusinformation')}`,
      component: (
        <Expandable variant="report" expanded titleComponent={t('order.orderReportDetails.tab.paybonusinformation')}>
          <OrderDetailsReportPayBonusInfo payandbonusDetails={payBonusDetails} />
        </Expandable>
      ),
    },
    {
      label: `${t('order.orderReportDetails.tab.mealsincidentals')}`,
      component: (
        <Expandable variant="report" expanded titleComponent={t('order.orderReportDetails.tab.mealsincidentals')}>
          <OrderDetailsReportMealsInfo mealsDetails={mealDetails} />
        </Expandable>
      ),
    },
    {
      label: `${t('order.orderReportDetails.tab.housing')}`,
      component: (
        <Expandable variant="report" expanded titleComponent={t('order.orderReportDetails.tab.housing')}>
          <OrderDetailsReportHousingInfo housingDetails={housingDetails} />
        </Expandable>
      ),
    },
    {
      label: `${t('order.orderReportDetails.tab.facility')}`,
      component: (
        <Expandable
          variant="report"
          expanded
          titleComponent={t('order.orderReportDetails.tab.facility') + ' - ' + facilityDetails.facilityName}
        >
          <OrderDetailsReportFacilityInfo facilityDetails={facilityDetails} />
        </Expandable>
      ),
    },
    {
      label: `${t('order.orderReportDetails.tab.unit')}`,
      component: (
        <Expandable variant="report" expanded titleComponent={t('order.orderReportDetails.tab.unit') + ' - ' + unitDetails.unitName}>
          <OrderDetailsReportUnitInfo unitDetails={unitDetails} />
        </Expandable>
      ),
    },
    {
      label: `${t('order.orderReportDetails.tab.travelinformation')}`,
      component: (
        <Expandable variant="report" expanded titleComponent={t('order.orderReportDetails.tab.travelinformation')}>
          <OrderDetailsReportTravelInfo travelDetails={travelDetails} />
        </Expandable>
      ),
    },
    {
      label: `${t('order.orderReportDetails.tab.pointsofinterest')}`,
      component: (
        <Expandable variant="report" expanded titleComponent={t('order.orderReportDetails.tab.pointsofinterest')}>
          <OrderDetailsReportPointOfInterest poiDetails={pointOfIntrests} />
        </Expandable>
      ),
    },
  ];

  return <GenericTabs tabs={tabs} orientation={Orientations.verticalList} />;
};
